import skyMaps from '../../data/skyMaps';
import { buildProjector, starColor, starRadius, starOpacity, layoutLabels } from '../shared/skyChart';

// A brand-styled star chart. Given an object and its precomputed map data
// (a framed field of real HYG catalogue stars + Stellarium constellation
// figures), it plots the region, draws the constellation lines, and marks the
// target with a red reticle. Pure SVG with no browser APIs, so it renders
// identically at build time (prerender) and in the browser.

const WIDTH = 640;
const HEIGHT = 480;
const PAD = 30;

const SkyMap = ({ object }) => {
    const map = object?.mapKey ? skyMaps[object.mapKey] : null;

    if (!map) {
        return null;
    }

    const project = buildProjector(map.frame, WIDTH, HEIGHT, PAD);
    const stars = map.stars || [];
    const lines = map.lines || [];
    const labels = map.labels || [];
    const nearby = map.nearby || [];
    const target = project(object.ra, object.dec);
    const inBounds = (p) => p.x >= PAD && p.x <= WIDTH - PAD && p.y >= PAD && p.y <= HEIGHT - PAD;

    // Gather every text label, then de-collide (target > nearby > star > con).
    const labelCandidates = [];
    labelCandidates.push({
        text: object.catalogIds?.[0] || object.primaryName,
        x: target.x, y: target.y - 16, size: 12, anchor: 'middle',
        className: 'sky-map-target-label', priority: 4
    });
    nearby.forEach((dsoObj) => {
        const p = project(dsoObj.ra, dsoObj.dec);
        if (inBounds(p)) {
            labelCandidates.push({
                text: dsoObj.name, x: p.x + 8, y: p.y + 3.5, size: 10, anchor: 'start',
                className: 'sky-map-dso-label', priority: 3
            });
        }
    });
    stars.filter((s) => s.name && s.mag < 3.9).forEach((star) => {
        const p = project(star.ra, star.dec);
        if (inBounds(p)) {
            labelCandidates.push({
                text: star.name, x: p.x + 6, y: p.y + 3, size: 10, anchor: 'start',
                className: 'sky-map-star-label', priority: 2
            });
        }
    });
    labels.forEach((label) => {
        const p = project(label.ra, label.dec);
        if (inBounds(p)) {
            labelCandidates.push({
                text: label.name.toUpperCase(), x: p.x, y: p.y, size: 10.5, anchor: 'middle',
                spaced: true, className: 'sky-map-con-label', priority: 1
            });
        }
    });
    const placedLabels = layoutLabels(labelCandidates);

    return (
        <figure className="sky-map">
            <svg
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                role="img"
                aria-label={`Sky chart showing ${object.primaryName} in the constellation ${object.constellation}`}
                xmlns="http://www.w3.org/2000/svg"
            >
                <defs>
                    <radialGradient id="sky-bg" cx="50%" cy="40%" r="80%">
                        <stop offset="0%" stopColor="#141a2e" />
                        <stop offset="55%" stopColor="#0d1120" />
                        <stop offset="100%" stopColor="#080a12" />
                    </radialGradient>
                    <filter id="star-glow" x="-200%" y="-200%" width="500%" height="500%">
                        <feGaussianBlur stdDeviation="1.4" result="b" />
                        <feMerge>
                            <feMergeNode in="b" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <filter id="target-glow" x="-200%" y="-200%" width="500%" height="500%">
                        <feGaussianBlur stdDeviation="3" />
                    </filter>
                </defs>

                <rect x="0" y="0" width={WIDTH} height={HEIGHT} rx="12" fill="url(#sky-bg)" />

                {/* faint coordinate grid (whole-degree dec / RA lines) */}
                <g stroke="#38415f" strokeWidth="0.5" strokeOpacity="0.35">
                    {[-10, -5, 0, 5, 10, 15, 20, 25, 30, 35].map((dec) => {
                        if (dec < map.frame.decMin || dec > map.frame.decMax) return null;
                        const a = project(map.frame.raMax, dec);
                        const b = project(map.frame.raMin, dec);
                        return <line key={`dec-${dec}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />;
                    })}
                    {Array.from({ length: 12 }, (_, i) => Math.ceil(map.frame.raMin / 5) * 5 + i * 5)
                        .filter((ra) => ra >= map.frame.raMin && ra <= map.frame.raMax)
                        .map((ra) => {
                            const a = project(ra, map.frame.decMax);
                            const b = project(ra, map.frame.decMin);
                            return <line key={`ra-${ra}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />;
                        })}
                </g>

                {/* constellation figures */}
                <g stroke="#8ea2e0" strokeWidth="1.1" strokeOpacity="0.5" strokeLinecap="round" fill="none">
                    {lines.map((seg, index) => {
                        const d = seg.points
                            .map((p, i) => {
                                const q = project(p[0], p[1]);
                                return `${i === 0 ? 'M' : 'L'}${q.x.toFixed(1)} ${q.y.toFixed(1)}`;
                            })
                            .join(' ');
                        return <path key={`seg-${index}`} d={d} />;
                    })}
                </g>

                {/* stars */}
                <g filter="url(#star-glow)">
                    {stars.map((star, index) => {
                        const p = project(star.ra, star.dec);
                        return (
                            <circle
                                key={`star-${index}`}
                                cx={p.x}
                                cy={p.y}
                                r={starRadius(star.mag)}
                                fill={starColor(star.ci)}
                                opacity={starOpacity(star.mag)}
                            />
                        );
                    })}
                </g>

                {/* nearby deep-sky object markers */}
                <g>
                    {nearby.map((dsoObj, index) => {
                        const p = project(dsoObj.ra, dsoObj.dec);
                        return (
                            <circle key={`dso-${index}`} cx={p.x} cy={p.y} r="4.5" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.3" strokeDasharray="2 2" />
                        );
                    })}
                </g>

                {/* target reticle with soft glow */}
                <g>
                    <circle cx={target.x} cy={target.y} r="11" fill="#E9A63D" opacity="0.2" filter="url(#target-glow)" />
                    {[[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([sx, sy], i) => (
                        <path
                            key={`corner-${i}`}
                            d={`M${target.x + sx * 10} ${target.y + sy * 4} L${target.x + sx * 10} ${target.y + sy * 10} L${target.x + sx * 4} ${target.y + sy * 10}`}
                            fill="none"
                            stroke="#E9A63D"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                    ))}
                </g>

                {/* de-collided text labels (drawn last, on top) */}
                <g>
                    {placedLabels.map((label, index) => (
                        <text
                            key={`lbl-${index}`}
                            x={label.x}
                            y={label.y}
                            className={label.className}
                            textAnchor={label.anchor}
                        >
                            {label.text}
                        </text>
                    ))}
                </g>
            </svg>

            <figcaption>
                {object.usesArticle ? 'The ' : ''}{object.primaryName} sits in <strong>{object.constellation}</strong>
                {object.constellationMeaning ? ` (${object.constellationMeaning})` : ''}.
                {nearby.length ? ' Faint circles mark nearby deep-sky objects.' : ''}
            </figcaption>
        </figure>
    );
};

export default SkyMap;
